import { useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Sparkles,
  Copy,
  Check,
  ExternalLink,
  Play,
  CheckCircle2,
  AlertCircle,
  Clock,
  FolderOpen,
  AlertTriangle,
  Layers,
  Cpu,
  BarChart3,
  Zap,
} from 'lucide-react';
import { Float16TrainingDetails, Float16TrainingJob, mockFloat16TrainingDetails } from '@/mock/gpu-instance';

interface Float16TrainingPanelProps {
  taskId: string;
}

const getMethodBadge = (method: Float16TrainingJob['method']) => {
  const colors: Record<Float16TrainingJob['method'], string> = {
    full: 'bg-red-500/10 text-red-600 border-red-500/30',
    lora: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
    qlora: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
  };
  const labels: Record<Float16TrainingJob['method'], string> = {
    full: 'Full Fine-tune',
    lora: 'LoRA',
    qlora: 'QLoRA',
  };
  return <Badge variant="outline" className={colors[method]}>{labels[method]}</Badge>;
};

const getStatusBadge = (status: Float16TrainingJob['status']) => {
  switch (status) {
    case 'training':
      return (
        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30">
          <Play className="h-3 w-3 mr-1" /> Training
        </Badge>
      );
    case 'completed':
      return (
        <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
          <CheckCircle2 className="h-3 w-3 mr-1" /> Completed
        </Badge>
      );
    case 'failed':
      return (
        <Badge className="bg-red-500/10 text-red-600 border-red-500/30">
          <AlertCircle className="h-3 w-3 mr-1" /> Failed
        </Badge>
      );
    case 'queued':
      return (
        <Badge className="bg-gray-500/10 text-gray-600 border-gray-500/30">
          <Clock className="h-3 w-3 mr-1" /> Queued
        </Badge>
      );
  }
};

const Float16TrainingPanel = ({ taskId }: Float16TrainingPanelProps) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const details: Float16TrainingDetails = useMemo(() => mockFloat16TrainingDetails(taskId), [taskId]);

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const trainingJobs = details.jobs.filter((j) => j.status === 'training').length;
  const completedJobs = details.jobs.filter((j) => j.status === 'completed').length;

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="border-purple-500/50 mt-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Sparkles className="h-6 w-6 text-purple-500" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  Float16 Training Framework
                  <Badge
                    variant="outline"
                    className={
                      details.status === 'active'
                        ? 'bg-green-500/10 text-green-600 border-green-500/30'
                        : 'bg-gray-500/10 text-gray-600'
                    }
                  >
                    {details.status}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Optimized LLM fine-tuning for enterprise workloads
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="font-mono">
                v{details.frameworkVersion}
              </Badge>
              <Button size="sm" variant="outline" onClick={() => window.open(details.notebookUrl, '_blank')}>
                <ExternalLink className="h-4 w-4 mr-1" />
                Open Jupyter
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-blue-600">{trainingJobs}</div>
            <div className="text-sm text-muted-foreground">Training</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-green-600">{completedJobs}</div>
            <div className="text-sm text-muted-foreground">Completed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold">{details.jobs.length}</div>
            <div className="text-sm text-muted-foreground">Total Jobs</div>
          </CardContent>
        </Card>
        {details.wandbProject && (
          <Card>
            <CardContent className="pt-6 text-center">
              <div className="flex items-center justify-center gap-1">
                <BarChart3 className="h-5 w-5 text-yellow-600" />
              </div>
              <div className="text-sm text-muted-foreground mt-1">W&B Tracking</div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Training Jobs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Fine-tuning Jobs</CardTitle>
          <CardDescription>Active and completed LLM fine-tuning jobs</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {details.jobs.map((job) => (
            <Card key={job.name} className="bg-muted/30">
              <CardContent className="pt-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Zap className="h-5 w-5 text-purple-500" />
                    <div>
                      <div className="font-medium">{job.name}</div>
                      <div className="flex items-center gap-2 mt-1">
                        {getMethodBadge(job.method)}
                        <span className="text-xs text-muted-foreground font-mono">{job.baseModel}</span>
                      </div>
                    </div>
                  </div>
                  {getStatusBadge(job.status)}
                </div>

                {job.status === 'training' && (
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">
                        Step {job.metrics.step.toLocaleString()}/{job.metrics.totalSteps.toLocaleString()}
                      </span>
                    </div>
                    <Progress value={job.progress} className="h-2" />
                  </div>
                )}

                {/* Config & Metrics */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Training Config */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground flex items-center gap-1">
                      <Cpu className="h-3 w-3" /> Training Config
                    </Label>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="p-2 bg-background rounded">
                        <span className="text-muted-foreground">LR: </span>
                        <span className="font-mono">{job.config.learningRate}</span>
                      </div>
                      <div className="p-2 bg-background rounded">
                        <span className="text-muted-foreground">Batch: </span>
                        <span className="font-mono">{job.config.batchSize}</span>
                      </div>
                      {job.config.loraRank && (
                        <div className="p-2 bg-background rounded">
                          <span className="text-muted-foreground">LoRA r: </span>
                          <span className="font-mono">{job.config.loraRank}</span>
                        </div>
                      )}
                      {job.config.loraAlpha && (
                        <div className="p-2 bg-background rounded">
                          <span className="text-muted-foreground">LoRA α: </span>
                          <span className="font-mono">{job.config.loraAlpha}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Metrics */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground flex items-center gap-1">
                      <BarChart3 className="h-3 w-3" /> Metrics
                    </Label>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="p-2 bg-background rounded text-center">
                        <div className="text-muted-foreground">Train Loss</div>
                        <div className="font-mono font-medium text-blue-600">
                          {job.metrics.loss.toFixed(3)}
                        </div>
                      </div>
                      {job.metrics.evalLoss && (
                        <div className="p-2 bg-background rounded text-center">
                          <div className="text-muted-foreground">Eval Loss</div>
                          <div className="font-mono font-medium text-green-600">
                            {job.metrics.evalLoss.toFixed(3)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </CardContent>
      </Card>

      {/* Paths */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            Workspace Paths
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Dataset Path</Label>
            <div className="relative">
              <pre className="bg-muted p-3 rounded-lg text-sm font-mono pr-12">
                {details.datasetPath}
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-1 right-1 h-8"
                onClick={() => handleCopy(details.datasetPath, 'dataset')}
              >
                {copiedField === 'dataset' ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Checkpoints Path</Label>
            <div className="relative">
              <pre className="bg-muted p-3 rounded-lg text-sm font-mono pr-12">
                {details.checkpointsPath}
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-1 right-1 h-8"
                onClick={() => handleCopy(details.checkpointsPath, 'checkpoints')}
              >
                {copiedField === 'checkpoints' ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </div>
          {details.wandbProject && (
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">W&B Project</Label>
              <div className="relative">
                <pre className="bg-muted p-3 rounded-lg text-sm font-mono pr-12">
                  {details.wandbProject}
                </pre>
                <Button
                  size="sm"
                  variant="ghost"
                  className="absolute top-1 right-1 h-8"
                  onClick={() => handleCopy(details.wandbProject!, 'wandb')}
                >
                  {copiedField === 'wandb' ? <Check className="size-4" /> : <Copy className="size-4" />}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Demo Warning */}
      <Alert className="border-yellow-500/50 bg-yellow-500/5">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-700">
          <span className="font-medium">Demo Mode:</span> This is simulated data. In production,
          training metrics and W&B integration will update in real-time.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default Float16TrainingPanel;
